import { App } from "antd"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../../../../hooks/use-date-formatter.tsx"
import { buildGanttPdfBlob, type GanttPdfResult } from "./build-gantt-pdf.tsx"
import { GanttPdfExportModal } from "./GanttPdfExportModal.tsx"

export function useGanttPdfExport({ projectName }: { projectName?: string }) {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  // @react-pdf/renderer builds its document via `pdf(<.../>)`, a separate
  // renderer instance the app's Redux store never reaches -- the account's
  // date format has to be resolved here, in a real hook, and handed down as
  // an already-formatted string.
  const { formatDate } = useDateFormatter()
  const leftPanelRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<HTMLDivElement>(null)
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<GanttPdfResult | null>(null)

  const generate = useCallback(async () => {
    if (!leftPanelRef.current || !chartRef.current) return
    setGenerating(true)
    try {
      const built = await buildGanttPdfBlob({
        leftPanelEl: leftPanelRef.current,
        chartEl: chartRef.current,
        projectName,
        generatedAtLabel: formatDate(new Date()) ?? ""
      })
      setResult(built)
    } catch (e) {
      console.error("Generate Gantt PDF error: ", e)
      notification.error({ message: t("milestonesGanttPdf.generateFailed") })
    } finally {
      setGenerating(false)
    }
  }, [projectName, notification, t, formatDate])

  const closeModal = useCallback(() => setResult(null), [])

  return {
    leftPanelRef,
    chartRef,
    generating,
    generate,
    modal: (
      <GanttPdfExportModal
        open={!!result}
        blob={result?.blob ?? null}
        scaledDown={!!result?.scaledDown}
        projectName={projectName}
        onClose={closeModal}
      />
    )
  }
}
